import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';

import { TableCell, TableRow, Tooltip } from '@mui/material';

import { Flipped } from 'react-flip-toolkit';
import { useDroppable } from '@dnd-kit/core';

import { LockOutlined, VisibilityOffOutlined } from '@mui/icons-material';

import { upperFirst } from 'lodash';

import { useDropzone } from 'react-dropzone';

import type { DocumentType } from '@/gql';
import { graphql } from '@/gql';

import type { OwnerPermisson } from '@/modules/files/hooks/useFilePermissions';
import { useFilePermissions } from '@/modules/files/hooks/useFilePermissions';

import { useFileUploader } from '@/modules/files/hooks/useFileUploader';

import { isRestrictedSegment } from '../toolbar/toolbarUtils';

import type { FileManagerUploadingState } from '../FileManagerUploadingBar';

import { FilenameCell } from './file-row/FilenameCell';
import { FileLastModifiedCell } from './file-row/FileLastModifiedCell';
import { FileOwnerSelector } from './file-row/FileOwnerSelector';
import { FileOptionsComponent } from './file-row/FileOptionsComponent';

export const FILE_ROW_FRAGMENT = graphql(`
  fragment fileRowFields on FilesForOwner {
    ...fileOptionsComponentFilesForOwnerFields
    ...fileOwnerSelectorFilesForOwnerFields
    owner {
      ... on Group {
        _id
        name
        wholeGroupGeneralName
      }
      ... on Service {
        _id
        name
      }
    }
    parent {
      ...fileOwnerSelectorParentFields
    }
    children {
      _id
      name
    }
  }
`);

const FILE_ROW_FILE_FRAGMENT = graphql(`
  fragment fileRowFileFields on File {
    ...fileOptionsComponentFileFields
    ...fileOwnerSelectorFileFields
  }
`);

interface Props {
  file: DocumentType<typeof FILE_ROW_FILE_FRAGMENT>;
  rootOwnerId: string;
  ownerPermissions: OwnerPermisson[];
  filesForOwner: DocumentType<typeof FILE_ROW_FRAGMENT>;
  canRestrictVisibility: boolean;
  selected: boolean;
  setUploadingState: Dispatch<SetStateAction<FileManagerUploadingState>>;
}

export const FileRow = ({
  file,
  rootOwnerId,
  filesForOwner,
  ownerPermissions,
  canRestrictVisibility,
  selected,
  setUploadingState,
}: Props) => {
  const { parent, owner, children } = filesForOwner;

  const permission = useFilePermissions(file, ownerPermissions, owner?._id);

  const [renameFileOpen, setRenameFileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const wholeGroupName = upperFirst(
    owner?.__typename === 'Group' && owner?.wholeGroupGeneralName
      ? owner.wholeGroupGeneralName
      : 'everyone',
  );

  const ownerCandidates = [
    {
      __typename: owner?.__typename,
      _id: owner?._id,
      name: wholeGroupName,
      isRoot: true,
    },
    ...(children ?? []),
  ];

  // Handles dropping files and folders into folders within GovernorHub
  const { isOver, setNodeRef } = useDroppable({
    id: file._id ?? '',
    data: { file, ownerId: file?.owner?._id },
    disabled: !permission.canEdit || !file.folder,
  });

  // Handles uploading files from the user's file system into folders in GovernorHub
  const { handleUploadRequestFromDrop } = useFileUploader({
    segment: {
      _id: file._id,
      file,
      fileId: file._id,
      folder: file.folder,
      name: file.filename,
      ownerId: owner?._id,
      ownerType: owner?.__typename,
      ownerName: owner?.name,
    },
    setUploadingState,
    rootOwnerId,
  });

  const { getRootProps, isDragAccept } = useDropzone({
    onDrop: async (acceptedFiles) => {
      return handleUploadRequestFromDrop(acceptedFiles);
    },
    noDragEventsBubbling: true,
    noClick: true,
    disabled: !permission.canEdit || !file.folder,
  });

  const ownerPermission =
    ownerPermissions.find((item) => item && item._id === owner?._id) ?? null;

  const { canEdit, canLockRoot } = permission;

  const isRestricted = isRestrictedSegment(
    { fileId: file._id, ownerId: file?.owner?._id || null },
    owner?._id,
    owner?.__typename,
  );

  const isParentRestricted = isRestrictedSegment(
    { fileId: file.parent?._id, ownerId: parent?.owner?._id || null },
    owner?._id,
    owner?.__typename,
  );

  return (
    <Flipped flipId={file._id ?? undefined}>
      <TableRow
        selected={selected || menuOpen || isDragAccept || isOver}
        component="tr"
        {...getRootProps()}
        ref={setNodeRef}
      >
        <FilenameCell
          file={file}
          canEdit={canEdit}
          ownerCandidates={ownerCandidates}
          isFirstRestricted={isRestricted && !isParentRestricted}
          renameFileOpen={renameFileOpen}
          closeRenameFile={() => setRenameFileOpen(false)}
        />
        <TableCell>
          {file.adminLock && (
            <Tooltip title="Only admins can rename, move or delete locked documents">
              <LockOutlined />
            </Tooltip>
          )}
          {file.preventDownload && (
            <Tooltip title="This file can only be downloaded by admins">
              <VisibilityOffOutlined />
            </Tooltip>
          )}
        </TableCell>
        <FileLastModifiedCell file={file} />
        {canRestrictVisibility && (
          <TableCell>
            <FileOwnerSelector
              file={file}
              filesForOwner={filesForOwner}
              parent={parent}
              rootOwnerId={rootOwnerId}
              ownerCandidates={ownerCandidates}
              canEdit={canEdit}
              canLockRoot={canLockRoot}
              wholeGroupName={wholeGroupName}
            />
          </TableCell>
        )}
        <TableCell>
          <FileOptionsComponent
            file={file}
            filesForOwner={filesForOwner}
            permission={permission}
            ownerPermission={ownerPermission}
            ownerCandidates={ownerCandidates}
            rootOwnerId={rootOwnerId}
            menuOpen={menuOpen}
            setMenuOpen={setMenuOpen}
            openRenameFile={() => setRenameFileOpen(true)}
          />
        </TableCell>
      </TableRow>
    </Flipped>
  );
};
